import { Fragment, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { hasRole, isStaff, useMe, useSession } from '../lib/session';
import { fmtTime, useNow } from '../lib/format';
import { cx } from './ui';

interface MarketRow {
  id: string;
  code: string;
  timezone: string;
  sessionsToday: { open: string; close: string }[];
}
interface Inbox {
  unread: number;
  items: { id: string; title: string; body: string; link: string | null; critical: boolean; readAt: string | null; createdAt: string }[];
}

const CLOCKS: { label: string; tz: string; market?: string }[] = [
  { label: 'PSX · Karachi', tz: 'Asia/Karachi', market: 'PSX' },
  { label: 'London', tz: 'Europe/London', market: 'FX' },
  { label: 'New York', tz: 'America/New_York', market: 'US' },
  { label: 'Sydney', tz: 'Australia/Sydney' },
];

function Clocks() {
  const now = useNow(15_000);
  const { data: markets } = useQuery({ queryKey: ['markets'], queryFn: () => api<MarketRow[]>('/markets'), staleTime: 300_000 });
  const open = (code?: string) => {
    const m = markets?.find((x) => x.code === code);
    if (!m) return null;
    return m.sessionsToday.some((w) => new Date(w.open).getTime() <= now && now < new Date(w.close).getTime());
  };
  return (
    <div className="hidden flex-wrap gap-2 lg:flex">
      {CLOCKS.map((c) => {
        const o = open(c.market);
        return (
          <div key={c.label} className="flex min-w-[92px] flex-col rounded-lg border border-line bg-panel px-3 py-1.5">
            <span className="text-[11px] uppercase tracking-[0.06em] text-muted">{c.label}</span>
            <span className="font-mono text-[15px] font-medium">{fmtTime(new Date(now), c.tz)}</span>
            {c.market && <span className={cx('text-[11px]', o ? 'text-up' : 'text-muted')}>{o === null ? '' : o ? `${c.market} open` : `${c.market} closed`}</span>}
          </div>
        );
      })}
    </div>
  );
}

function Bell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['notifications'], queryFn: () => api<Inbox>('/notifications'), refetchInterval: 60_000 });
  const me = useMe();
  const read = useMutation({ mutationFn: () => api('/notifications/read', { body: {} }), onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }) });
  const tz = me.data?.timezone ?? 'UTC';
  return (
    <div className="relative">
      <button
        type="button"
        aria-label={`Notifications, ${data?.unread ?? 0} unread`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl border border-line bg-panel text-ink"
      >
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {!!data?.unread && <span className="absolute top-1.5 right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-down px-1 text-[10px] font-bold text-bg">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 flex max-h-[70vh] w-[min(380px,90vw)] flex-col overflow-hidden rounded-xl border border-line2 bg-raised shadow-2xl">
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <span className="font-semibold">Notifications</span>
            <button type="button" className="cursor-pointer text-sm text-accent" onClick={() => read.mutate()}>
              Mark all read
            </button>
          </div>
          <div className="overflow-y-auto">
            {!data?.items.length && <p className="px-4 py-6 text-center text-sub">Nothing yet.</p>}
            {data?.items.map((n) => (
              <Link key={n.id} to={n.link ?? '/'} onClick={() => setOpen(false)} className={cx('flex flex-col gap-0.5 border-b border-line px-4 py-3 text-ink no-underline', !n.readAt && 'bg-[#141a24]')}>
                <span className={cx('font-semibold', n.critical && 'text-warn')}>{n.title}</span>
                <span className="text-sm text-sub">{n.body}</span>
                <span className="text-xs text-muted">{fmtTime(n.createdAt, tz, true)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface NavItem {
  to: string;
  label: string;
  show: boolean;
}
interface NavGroup {
  label: string | null;
  items: NavItem[];
}

/** Grouped so the menu stays scannable as screens are added: the desk, the person's own things, then administration. */
function navGroups(roles: string[] | undefined): NavGroup[] {
  const groups: NavGroup[] = [
    {
      label: null,
      items: [
        { to: '/', label: 'Today', show: hasRole(roles, 'TRADER') },
        { to: '/floor', label: 'Floor', show: true },
        { to: '/analysis', label: 'Analysis', show: hasRole(roles, 'TRADER') },
        { to: '/setups', label: 'Setups', show: true },
        { to: '/plans/new', label: 'New trade plan', show: hasRole(roles, 'TRADER') },
        { to: '/trades', label: 'Trades', show: true },
        { to: '/handovers', label: 'Handovers', show: hasRole(roles, 'TRADER', 'MASTER_TRADER') },
        { to: '/rooms', label: 'Rooms', show: true },
        { to: '/webinars', label: 'Webinars', show: true },
        { to: '/library', label: 'Library', show: true },
        { to: '/risk', label: 'Risk desk', show: isStaff(roles) },
        { to: '/compliance', label: 'Compliance', show: hasRole(roles, 'MASTER_TRADER', 'ADMIN', 'RISK_MANAGER') },
        { to: '/analytics', label: 'Analytics', show: hasRole(roles, 'TRADER', 'MASTER_TRADER', 'RISK_MANAGER', 'ADMIN') },
      ],
    },
    {
      label: 'You',
      items: [
        { to: '/symbols', label: 'My symbols', show: hasRole(roles, 'TRADER', 'MASTER_TRADER') },
        { to: '/reminders', label: 'Reminders', show: true },
        { to: '/settings', label: 'Settings', show: true },
      ],
    },
    {
      label: 'Administration',
      items: [
        { to: '/admin', label: 'Admin', show: hasRole(roles, 'ADMIN', 'MASTER_TRADER') },
        { to: '/admin/enrolment', label: 'Enrolment', show: hasRole(roles, 'ADMIN') },
        { to: '/admin/symbol-requests', label: 'Symbol requests', show: hasRole(roles, 'ADMIN', 'MASTER_TRADER') },
        { to: '/admin/markets', label: 'Markets & symbols', show: hasRole(roles, 'ADMIN', 'MASTER_TRADER') },
        { to: '/admin/signals', label: 'Signal sources', show: hasRole(roles, 'ADMIN', 'MASTER_TRADER') },
        { to: '/admin/streaming', label: 'Streaming', show: hasRole(roles, 'ADMIN') },
        { to: '/publishing', label: 'YouTube publishing', show: hasRole(roles, 'ADMIN', 'MASTER_TRADER', 'RISK_MANAGER') },
        { to: '/admin/settings', label: 'Settings console', show: isStaff(roles) },
        { to: '/admin/fx', label: 'FX rates', show: hasRole(roles, 'ADMIN') },
        { to: '/admin/audit', label: 'Audit log', show: hasRole(roles, 'ADMIN') },
        { to: '/admin/deliveries', label: 'Delivery log', show: hasRole(roles, 'ADMIN') },
      ],
    },
  ];
  return groups.map((g) => ({ ...g, items: g.items.filter((n) => n.show) })).filter((g) => g.items.length);
}

function NavLinks({ groups, onNavigate }: { groups: NavGroup[]; onNavigate?: () => void }) {
  return (
    <div className="flex flex-col gap-1">
      {groups.map((g) => (
        <Fragment key={g.label ?? 'main'}>
          {g.label && <span className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-[0.08em] text-muted">{g.label}</span>}
          {g.items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/' || n.to === '/admin'}
              onClick={onNavigate}
              className={({ isActive }) => cx('flex min-h-11 items-center rounded-lg px-3 no-underline', isActive ? 'bg-accent-bg font-semibold text-ink' : 'text-sub hover:text-ink')}
            >
              {n.label}
            </NavLink>
          ))}
        </Fragment>
      ))}
    </div>
  );
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 text-ink no-underline">
      <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg border border-[#2b4a78] bg-accent-bg font-mono text-xs font-semibold text-accent">VTF</div>
      {!compact && (
        <div className="flex flex-col">
          <span className="font-semibold">Virtual Trading Floor</span>
          <span className="text-xs text-muted">Private desk</span>
        </div>
      )}
      {compact && <span className="font-semibold">Trading Floor</span>}
    </Link>
  );
}

/** `touch`: a full-size Sign out button for phones (44 px target) instead of the small sidebar link. */
function UserCard({ name, onSignOut, touch = false }: { name: string | undefined; onSignOut: () => void; touch?: boolean }) {
  const initials = (name ?? '').split(' ').map((p) => p[0]).join('').slice(0, 2);
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-line bg-panel p-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2a2f3a] text-xs font-semibold">{initials}</div>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-semibold">{name}</span>
        {!touch && (
          <button type="button" onClick={onSignOut} className="min-h-6 cursor-pointer self-start text-left text-xs text-muted hover:text-ink">
            Sign out
          </button>
        )}
      </div>
      {touch && (
        <button type="button" onClick={onSignOut} className="min-h-11 shrink-0 cursor-pointer rounded-lg border border-line2 px-4 text-sm font-semibold text-ink">
          Sign out
        </button>
      )}
    </div>
  );
}

/** Phones: a compact top bar; the menu opens as a full-screen panel and closes on navigation or Escape. */
function PhoneBar({ groups, name, onSignOut }: { groups: NavGroup[]; name: string | undefined; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      closeButton.current?.focus();
      return () => {
        document.body.style.overflow = prev;
      };
    }
    if (wasOpen.current) menuButton.current?.focus();
    return undefined;
  }, [open]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') return setOpen(false);
    if (e.key !== 'Tab' || !panel.current) return;
    // keep focus inside the open menu
    const f = panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };

  return (
    <header className="sticky top-0 z-30 flex items-center gap-2 border-b border-line bg-nav px-4 py-2.5 md:hidden">
      <Brand compact />
      <div className="ml-auto flex items-center gap-2">
        <Bell />
        <button
          ref={menuButton}
          type="button"
          aria-label="Menu"
          aria-expanded={open}
          aria-controls="phone-menu"
          onClick={() => setOpen(true)}
          className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl border border-line bg-panel text-ink"
        >
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
      </div>
      {open && (
        <div ref={panel} id="phone-menu" role="dialog" aria-modal="true" aria-label="Menu" onKeyDown={onKeyDown} className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-nav px-4 pt-2.5 pb-6">
          <div className="flex items-center gap-2 pb-2">
            <Brand compact />
            <button
              ref={closeButton}
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="ml-auto flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl border border-line bg-panel text-ink"
            >
              <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <nav aria-label="Main">
            <NavLinks groups={groups} onNavigate={() => setOpen(false)} />
          </nav>
          <div className="mt-6">
            <UserCard name={name} onSignOut={onSignOut} touch />
          </div>
        </div>
      )}
    </header>
  );
}

export function Shell() {
  const me = useMe();
  const { signOut } = useSession();
  const groups = navGroups(me.data?.roles);
  const onSignOut = () => void signOut();

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <PhoneBar groups={groups} name={me.data?.name} onSignOut={onSignOut} />
      <nav aria-label="Main" className="sticky top-0 hidden h-screen w-[232px] shrink-0 flex-col gap-1 overflow-y-auto border-r border-line bg-nav px-3.5 py-4 md:flex">
        <div className="px-2 pb-3">
          <Brand />
        </div>
        <NavLinks groups={groups} />
        <div className="mt-auto pt-4">
          <UserCard name={me.data?.name} onSignOut={onSignOut} />
        </div>
      </nav>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="hidden items-center justify-end gap-2 px-7 pt-4 md:flex">
          <Clocks />
          <Bell />
        </div>
        <main className="flex min-w-0 flex-col gap-5 px-4 pt-4 pb-10 sm:px-7 md:pt-3">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
